import { X, Printer, Download } from "lucide-react";
import { money } from "@/lib/api";

const COMPANY = {
  name: "THE WORLD PROMOTIONS",
  website: "www.theworldpromotions.com",
  emails: ["info@theworldpromotions.com", "support@theworldpromotions.com"],
  whatsapp: "+250 790 268 952",
  socials: ["Facebook", "Instagram", "X", "YouTube", "TikTok"],
};

const STATUS_STYLE = {
  REQUESTED: "bg-slate-100 text-slate-700 border-slate-300",
  UNDER_REVIEW: "bg-amber-100 text-amber-700 border-amber-300",
  APPROVED: "bg-sky-100 text-sky-700 border-sky-300",
  PROCESSING: "bg-indigo-100 text-indigo-700 border-indigo-300",
  PAID: "bg-emerald-100 text-emerald-700 border-emerald-300",
  FAILED: "bg-rose-100 text-rose-700 border-rose-300",
  REJECTED: "bg-rose-100 text-rose-700 border-rose-300",
  CANCELLED: "bg-slate-100 text-slate-500 border-slate-300",
};

function Row({ label, value, strong, negative, positive }) {
  return (
    <div className="flex items-center justify-between py-1 text-sm">
      <span className="text-slate-500">{label}</span>
      <span className={`text-right ${strong ? "font-bold" : "font-medium"} ${negative ? "text-rose-600" : positive ? "text-emerald-600" : "text-slate-800"}`}>{value}</span>
    </div>
  );
}

export function WithdrawalReceipt({ receipt, onClose }) {
  if (!receipt) return null;
  const r = receipt;
  const feePct = r.withdrawal_fee_pct ?? 10;

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-black/70 p-3 sm:p-6" onClick={onClose} data-testid="withdrawal-receipt-overlay">
      <style>{`@media print { body * { visibility: hidden !important; } #twp-receipt, #twp-receipt * { visibility: visible !important; } #twp-receipt { position: absolute !important; inset: 0 !important; margin: 0 !important; box-shadow: none !important; border: none !important; max-width: 100% !important; } .twp-noprint { display: none !important; } }`}</style>
      <div onClick={(e) => e.stopPropagation()} className="my-4 w-full max-w-[640px]">
        <div className="twp-noprint mb-2 flex items-center justify-end gap-2">
          <button onClick={() => window.print()} data-testid="receipt-print" className="flex items-center gap-1.5 rounded-lg bg-white/10 px-3 py-1.5 text-sm font-medium text-white hover:bg-white/20"><Printer className="h-4 w-4" /> Imprimer</button>
          <button onClick={() => window.print()} data-testid="receipt-download" className="flex items-center gap-1.5 rounded-lg bg-white/10 px-3 py-1.5 text-sm font-medium text-white hover:bg-white/20"><Download className="h-4 w-4" /> Télécharger (PDF)</button>
          <button onClick={onClose} data-testid="receipt-close" className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/10 text-white hover:bg-white/20"><X className="h-5 w-5" /></button>
        </div>

        <div id="twp-receipt" className="overflow-hidden rounded-2xl bg-white text-slate-800 shadow-2xl" data-testid="withdrawal-receipt">
          {/* Header */}
          <div className="bg-slate-900 px-6 py-5 text-white">
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="The World Promotions" className="h-11 w-11 rounded-full bg-white/5 object-contain" />
              <div className="leading-tight">
                <div className="font-display text-lg font-bold tracking-wide">{COMPANY.name}</div>
                <div className="text-[11px] text-slate-300">{COMPANY.website}</div>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-x-4 gap-y-0.5 text-[11px] text-slate-300">
              {COMPANY.emails.map((e) => <span key={e}>{e}</span>)}
              <span>WhatsApp : {COMPANY.whatsapp}</span>
            </div>
          </div>

          {/* Title */}
          <div className="border-b border-slate-100 px-6 py-4">
            <h2 className="font-display text-xl font-bold text-slate-900">REÇU DE PAIEMENT</h2>
            <p className="text-xs text-slate-500">{COMPANY.name}</p>
            <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-sm">
              <div><span className="text-slate-500">Référence : </span><span className="font-mono font-semibold" data-testid="receipt-reference">{r.reference}</span></div>
              <div className="text-slate-500">Date : <span className="font-medium text-slate-700">{r.created_date}</span> · Heure : <span className="font-medium text-slate-700">{r.created_time}</span></div>
            </div>
          </div>

          {/* Member info */}
          <div className="px-6 py-4">
            <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Informations du membre</div>
            <Row label="Nom complet" value={r.full_name || "—"} />
            <Row label="E-mail" value={r.email || "—"} />
            <Row label="Member Code" value={r.member_code || "—"} />
          </div>

          {/* Withdrawal details */}
          <div className="border-t border-slate-100 px-6 py-4">
            <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Détails du retrait</div>
            <Row label="Méthode" value={r.method === "usdt" ? "USDT" : "Mobile Money"} />
            {r.method === "usdt" ? (
              <>
                <Row label="Réseau" value={r.usdt_network || "TRC20"} />
                <Row label="Adresse wallet" value={r.coordinates_masked || "—"} />
              </>
            ) : (
              <>
                <Row label="Pays" value={r.mm_country || "—"} />
                <Row label="Opérateur" value={r.mm_operator || "—"} />
                <Row label="Numéro" value={r.coordinates_masked || "—"} />
              </>
            )}
          </div>

          {/* Financial detail */}
          <div className="border-t border-slate-100 bg-slate-50 px-6 py-4">
            <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Détail financier</div>
            <Row label="Montant demandé" value={money(r.requested_amount)} />
            <Row label={`Frais de retrait (${feePct}%)`} value={`-${money(r.withdrawal_fee)}`} negative />
            <div className="my-1 border-t border-slate-200" />
            <Row label="Montant net à recevoir" value={money(r.net_payout)} strong positive />
            <div className="mt-2 text-[11px] text-slate-500">Délai indicatif : {r.processing_time || "1–3 jours ouvrés"}</div>
          </div>

          {/* Status */}
          <div className="border-t border-slate-100 px-6 py-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-slate-600">Statut</span>
              <span data-testid="receipt-status" className={`rounded-md border px-3 py-1 text-xs font-bold ${STATUS_STYLE.PAID}`}>
                PAIEMENT CONFIRMÉ
              </span>
            </div>
            <div className="mt-3 space-y-1">
              {r.payment_reference && <Row label="Référence de paiement" value={r.payment_reference} />}
              {r.paid_at && <Row label="Date de paiement" value={(r.paid_at || "").slice(0, 16).replace("T", " ")} />}
            </div>
            <p className="mt-3 rounded-lg bg-emerald-50 p-3 text-xs text-emerald-700">
              Ce document confirme que le paiement du retrait indiqué ci-dessus a été traité avec succès par The World Promotions.
            </p>
          </div>

          {/* Footer */}
          <div className="bg-slate-900 px-6 py-5 text-center text-white">
            <div className="font-display text-sm font-bold tracking-wide">{COMPANY.name}</div>
            <div className="mt-1 text-[11px] text-slate-300">{COMPANY.website}</div>
            <div className="mt-1 flex flex-wrap justify-center gap-x-3 text-[11px] text-slate-300">
              {COMPANY.emails.map((e) => <span key={e}>{e}</span>)}
            </div>
            <div className="mt-1 text-[11px] text-slate-300">WhatsApp : {COMPANY.whatsapp}</div>
            <div className="mt-1 text-[11px] text-slate-400">{COMPANY.socials.join(" · ")}</div>
            <div className="mt-2 text-[10px] italic text-slate-500">Document électronique généré par The World Promotions.</div>
          </div>
        </div>
      </div>
    </div>
  );
}
